import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../../components/Panel'
import { TrendChart } from '../../../components/charts/TrendChart'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { GroupNote } from './GroupNote'
import { useDeskState } from './useDeskState'
import { Failed, Loading } from './ArticleBits'
import { deskApi, type AlertRuleCard, type DeskPub, type LadderMonth, type ScoreRow, type TrendSeries } from '../../../api/visitorsDesk'
import { PERIOD_OPTIONS } from '../../../lib/deskPeriod'
import { lastFiredText, ruleStateText } from '../../../lib/deskAlerts'
import { pubLabel } from '../../../lib/deskFormat'
import { td, th, mono } from '../../../lib/deskArticles'
import {
  LADDER_COLUMNS, TREND_METRICS, isTrendMetric, ladderRows, lastYearCell, pctValue, scoreChange, scoreValue, scoreVerdictText, seriesForChart,
} from '../../../lib/deskTrends'

const sel = 'min-h-[44px] md:min-h-[32px] max-w-full bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper'
const TONE = { up: 'text-radar-cyan', down: 'text-amber-glow', flat: 'text-paper-faint' } as const
const CHART_NOTE = 'Flags mark days an alert fired. Dotted end = the latest days aren’t finished yet. Before our own tracking started, the GA4 line comes from Google Analytics history and is marked GA4. It is shown beside our numbers, never blended into them.'
const SCORE_NOTE = '“Normal” means the change is within what this title usually swings week to week, so it isn’t worth reacting to. Only “above” or “below” deserve attention.'
const LADDER_NOTE = 'The truest growth measure: is the middle of the ladder getting thicker?'

/** `visitors/desk/:pub/trends`. */
export function DeskTrends() {
  return <DeskFrame tab="trends"><TrendsBody /></DeskFrame>
}

export function TrendsBody() {
  const { pub, q, sp, setSp } = useDeskState('trends')
  const p = pub as DeskPub
  const raw = sp.get('metric')
  const metric = isTrendMetric(raw) ? raw : 'people'
  const pick = (v: string) => setSp((prev) => { const n = new URLSearchParams(prev); n.set('metric', v); return n }, { replace: true })
  const periodLabel = PERIOD_OPTIONS.find((o) => o.value === q.period)?.label ?? 'This period'

  const series = useQuery({
    queryKey: ['desk', 'trends', 'series', pub, metric, q.group], queryFn: () => deskApi.trendsSeries(p, metric, undefined, q.group), retry: false, placeholderData: (prev) => prev,
  })
  const score = useQuery({
    queryKey: ['desk', 'trends', 'score', pub, q.period, q.from, q.to, q.compare, q.group], queryFn: () => deskApi.trendsScorecard(p, q), retry: false, placeholderData: (prev) => prev,
  })
  const ladder = useQuery({
    queryKey: ['desk', 'trends', 'ladder', pub, q.group], queryFn: () => deskApi.trendsLadder(p, 12, q.group), retry: false, placeholderData: (prev) => prev,
  })
  const alerts = useQuery({
    queryKey: ['desk', 'trends', 'alerts', pub, q.period, q.from, q.to, q.compare, q.group], queryFn: () => deskApi.alerts(p, q), retry: false, placeholderData: (prev) => prev,
  })
  const name = pubLabel(pub)

  return (
    <div className="space-y-4">
      <Panel label={`${TREND_METRICS.find((m) => m.value === metric)?.label ?? 'Trend'} per day`} accent="amber">
        <label className="mb-3 flex min-w-0 flex-col gap-1">
          <span className="label">Metric</span>
          <select aria-label="Metric" className={sel} value={metric} onChange={(e) => pick(e.target.value)}>
            {TREND_METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <GroupNote applied={series.data?.group_applied} />
        {series.isError ? <Failed what="the trend" /> : !series.data ? <Loading /> : series.data.unavailable !== undefined
          ? <NotYetLive text={series.data.unavailable} /> : <Line s={series.data} where={name} />}
        <p className="mt-2 text-[12px] text-paper-faint">{CHART_NOTE}</p>
      </Panel>

      <Panel label={`The scorecard · ${periodLabel.toLowerCase()}`} accent="cyan">
        <GroupNote applied={score.data?.group_applied} />
        {score.isError ? <Failed what="the scorecard" /> : !score.data ? <Loading /> : score.data.unavailable !== undefined
          ? <NotYetLive text={score.data.unavailable} /> : <Scorecard rows={score.data.rows} period={periodLabel} />}
        <p className="mt-2 text-[12px] text-paper-faint">{SCORE_NOTE}</p>
      </Panel>

      <Panel label="Reader ladder, month by month" accent="violet">
        <GroupNote applied={ladder.data?.group_applied} />
        {ladder.isError ? <Failed what="the reader ladder" /> : !ladder.data ? <Loading /> : ladder.data.unavailable !== undefined
          ? <NotYetLive text={ladder.data.unavailable} /> : <Ladder rows={ladder.data.rows} />}
        <p className="mt-2 text-[12px] text-paper-faint">{LADDER_NOTE}</p>
      </Panel>

      <Panel label="Alerts · sent to you by email and shown on Today" accent="amber">
        <GroupNote applied={(q.group ?? 'all') === 'all' ? undefined : false} />
        {alerts.isError ? <Failed what="the alerts" /> : !alerts.data ? <Loading /> : alerts.data.unavailable !== undefined
          ? <NotYetLive text={alerts.data.unavailable} /> : <AlertRules rules={alerts.data.rules} />}
        <p className="mt-3 text-[13px]"><Link to="/visitors/desk/settings" className="text-amber-glow hover:underline">Change alerts in Desk settings →</Link></p>
      </Panel>
    </div>
  )
}

function Line({ s, where }: { s: TrendSeries; where: string }) {
  const c = seriesForChart(s)
  const format = s.kind === 'rate' ? pctValue : undefined
  const last = [...s.points].reverse().find((x) => x.value !== null)
  const aria = `${s.label} per day for ${where}, ${c.labels.length} days${last ? `; latest ${last.day}: ${s.kind === 'rate' ? pctValue(last.value as number) : last.value}` : ''}`
  return <TrendChart series={c.series} labels={c.labels} band={c.band} incompleteFrom={c.incompleteFrom} markers={c.markers} ariaLabel={aria} format={format} />
}

export function Scorecard({ rows, period = 'This period' }: { rows: ScoreRow[]; period?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-[13px]">
        <thead>
          <tr>
            <th className={th}>Measure</th><th className={`${th} text-right`}>{period}</th><th className={`${th} text-right`}>Previous period</th>
            <th className={`${th} text-right`}>Change</th><th className={`${th} text-right`}>Same period last year</th><th className={`${th} text-right`}>Change</th>
            <th className={th}>Verdict</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const head = (
              <td className={`${td} font-semibold`}>
                {r.label}
                {r.incomplete && <div className="text-[11px] font-normal text-paper-faint">period not finished</div>}
              </td>
            )
            if (r.unavailable) {
              return <tr key={r.metric} className="border-t border-ink-600">{head}<td colSpan={6} className={`${td} text-paper-faint`}>{r.unavailable}</td></tr>
            }
            const c = scoreChange(r.kind, r.change, r.change_pct)
            const y = scoreChange(r.kind, r.last_year_change, r.last_year_change_pct)
            return (
              <tr key={r.metric} className="border-t border-ink-600">
                {head}
                <td className={`${td} text-right font-mono`} style={mono}>{scoreValue(r.kind, r.now)}</td>
                <td className={`${td} text-right font-mono text-paper-faint`} style={mono}>{scoreValue(r.kind, r.previous)}</td>
                <td className={`${td} text-right font-mono ${TONE[c.tone]}`} style={mono}>{c.text}</td>
                <td className={`${td} text-right font-mono text-paper-faint`} style={mono}>{lastYearCell(r)}</td>
                <td className={`${td} text-right font-mono ${TONE[y.tone]}`} style={mono}>{y.text}</td>
                <td className={td}><span className="whitespace-nowrap border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper-dim">{scoreVerdictText(r.verdict)}</span></td>
              </tr>
            )
          })}
          {rows.length === 0 && <tr><td colSpan={7} className={`${td} text-paper-faint`}>Nothing to show yet</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function Ladder({ rows }: { rows: LadderMonth[] }) {
  const lines = ladderRows(rows)
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[440px] text-[13px]">
        <thead><tr><th className={th}>Month</th>{LADDER_COLUMNS.map((c) => <th key={c.key} className={`${th} text-right`}>{c.label}</th>)}</tr></thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.month} className="border-t border-ink-600">
              <td className={td}>{l.month}</td>
              {l.cells.map((v, i) => <td key={LADDER_COLUMNS[i].key} className={`${td} text-right font-mono`} style={mono}>{v}</td>)}
            </tr>
          ))}
          {lines.length === 0 && <tr><td colSpan={6} className={`${td} text-paper-faint`}>No months rolled up yet</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function AlertRules({ rules }: { rules: AlertRuleCard[] }) {
  return (
    <ul className="divide-y divide-ink-600">
      {rules.map((r) => (
        <li key={r.rule} className="flex min-w-0 items-start justify-between gap-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="break-words text-[13px] font-semibold">{r.label}</div>
            <div className="break-words text-[12px] text-paper-faint">{r.text}</div>
          </div>
          <div className="shrink-0 text-right">
            <span role="img" aria-label={ruleStateText(r.enabled)} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-paper-dim">
              <span aria-hidden="true" className={`relative inline-block h-[18px] w-[30px] rounded-full ${r.enabled ? 'bg-radar-cyan/70' : 'bg-ink-500'}`}>
                <span className={`absolute top-[3px] h-3 w-3 rounded-full bg-paper ${r.enabled ? 'right-[3px]' : 'left-[3px]'}`} />
              </span>
              {ruleStateText(r.enabled)}
            </span>
            <div className="mt-1 text-[11px] text-paper-faint">{lastFiredText(r.last_fired)}</div>
          </div>
        </li>
      ))}
      {rules.length === 0 && <li className="py-2 text-[13px] text-paper-faint">No alert rules yet</li>}
    </ul>
  )
}
